import React, { useState, useEffect, useRef } from 'react';
import {
  Crown,
  Search,
  Filter,
  Edit,
  Key,
  Eye,
  UserCheck,
  UserX,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { superAdminAPI } from '../../services/api';
import SuperAdminModal from './SuperAdminModal';

const SuperAdminManagement = () => {
  const [superAdmins, setSuperAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedSuperAdmins, setSelectedSuperAdmins] = useState(new Set());
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedSuperAdmin, setSelectedSuperAdmin] = useState(null);

  const tableScrollRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => {
    fetchSuperAdmins();
  }, [currentPage, pageSize, searchTerm, selectedStatus]);

  const fetchSuperAdmins = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        size: pageSize,
        sortBy: 'createdAt',
        sortDir: 'desc'
      };

      // Add filters
      if (searchTerm) params.search = searchTerm;
      if (selectedStatus) params.status = selectedStatus;
      params.roles = 'ROLE_SUPER_ADMIN'; // Only fetch super admin users

      const response = await superAdminAPI.searchUsers(params);
      const data = response.data;

      setSuperAdmins(data.content || []);
      setTotalElements(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
    } catch (error) {
      console.error('Error fetching super admins:', error);
      setSuperAdmins([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSuperAdmin = (superAdmin) => {
    setSelectedSuperAdmin(superAdmin);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleViewSuperAdmin = (superAdmin) => {
    setSelectedSuperAdmin(superAdmin);
    setModalMode('view');
    setShowModal(true);
  };



  const handleResetPassword = async (superAdmin) => {
    if (window.confirm(`Reset password for super admin "${superAdmin.name}"?`)) {
      try {
        await superAdminAPI.resetUserPassword(superAdmin.id);
        alert('Password reset successfully. New password has been sent to the user\'s email.');
      } catch (error) {
        console.error('Error resetting password:', error);
        alert('Failed to reset password. Please try again.');
      }
    }
  };

  const handleToggleStatus = async (superAdmin) => {
    const newStatus = !superAdmin.enabled;
    const action = newStatus ? 'activate' : 'deactivate';

    if (window.confirm(`Are you sure you want to ${action} ${superAdmin.name}?`)) {
      try {
        await superAdminAPI.updateUserStatus(superAdmin.id, {
          active: newStatus
        });
        fetchSuperAdmins();
      } catch (error) {
        console.error('Error updating super admin status:', error);
        alert(`Failed to ${action} super admin. Please try again.`);
      }
    }
  };

  const handleModalSave = () => {
    setShowModal(false);
    fetchSuperAdmins();
  };



  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setCurrentPage(0);
  };

  // Bulk selection handlers
  const handleSelectSuperAdmin = (superAdminId) => {
    const newSelected = new Set(selectedSuperAdmins);
    if (newSelected.has(superAdminId)) {
      newSelected.delete(superAdminId);
    } else {
      newSelected.add(superAdminId);
    }
    setSelectedSuperAdmins(newSelected);
    setIsSelectAllChecked(newSelected.size === superAdmins.length && superAdmins.length > 0);
  };

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedSuperAdmins(new Set());
      setIsSelectAllChecked(false);
    } else {
      const allSuperAdminIds = new Set(superAdmins.map(superAdmin => superAdmin.id));
      setSelectedSuperAdmins(allSuperAdminIds);
      setIsSelectAllChecked(true);
    }
  };

  const filteredSuperAdmins = superAdmins.filter(superAdmin => {
    const matchesSearch = !searchTerm ||
      superAdmin.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      superAdmin.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = !selectedStatus ||
      (selectedStatus === 'active' && superAdmin.enabled) ||
      (selectedStatus === 'inactive' && !superAdmin.enabled);

    return matchesSearch && matchesStatus;
  });

  return (
    <div>
      <div className="page-header-compact">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold azure-text mb-1 flex items-center">
              <Crown className="h-6 w-6 mr-2 text-yellow-600" />
              Super Admin Management
            </h1>
            <p className="text-sm text-blue-700 font-medium">
              Manage super administrators with system-wide access
            </p>
          </div>
          <div className="flex space-x-3">
            <div className="text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
              <span className="font-medium">Note:</span> Super admins can only be created via database scripts
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search super admins..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={selectedStatus}
                onChange={handleStatusChange}
                className="input-field pl-10 pr-8"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Super Admins Table */}
      <div className="card">
        {loading ? (
          <CardLoadingSpinner message="Loading super admins..." />
        ) : (
          <div>
            {/* Table Container */}
            <div
              ref={tableScrollRef}
              className="table-container-fixed"
            >
              <div ref={tableRef}>
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="table-header w-10 sticky left-0 bg-slate-50 shadow-lg z-10">
                        <input
                          type="checkbox"
                          checked={isSelectAllChecked}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="table-header w-20 sticky left-10 bg-slate-50 shadow-lg z-10">Actions</th>
                      <th className="table-header">Name</th>
                      <th className="table-header">Email</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Last Login</th>
                      <th className="table-header">Created</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {filteredSuperAdmins.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center">
                          <div className="text-slate-500">
                            {superAdmins.length === 0 ? 'No super admins found. Add some super admins to get started.' : 'No super admins match your search criteria.'}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredSuperAdmins.map((superAdmin) => (
                        <tr key={superAdmin.id} className="hover:bg-slate-50 bg-yellow-50">
                          <td className="table-cell sticky left-0 bg-yellow-50 shadow-lg z-10">
                            <input
                              type="checkbox"
                              checked={selectedSuperAdmins.has(superAdmin.id)}
                              onChange={() => handleSelectSuperAdmin(superAdmin.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="table-cell sticky left-10 bg-yellow-50 shadow-lg z-10">
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => handleViewSuperAdmin(superAdmin)}
                                className="text-slate-600 hover:text-slate-900 p-1 hover:bg-slate-100 transition-colors duration-200"
                                title="View Super Admin"
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleEditSuperAdmin(superAdmin)}
                                className="text-slate-600 hover:text-slate-900 p-1 hover:bg-slate-100 transition-colors duration-200"
                                title="Edit Super Admin"
                              >
                                <Edit className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleResetPassword(superAdmin)}
                                className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 transition-colors duration-200"
                                title="Reset Password"
                              >
                                <Key className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleToggleStatus(superAdmin)}
                                className={`p-1 transition-colors duration-200 ${
                                  superAdmin.enabled
                                    ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50'
                                    : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                                }`}
                                title={superAdmin.enabled ? "Deactivate Super Admin" : "Activate Super Admin"}
                              >
                                {superAdmin.enabled ? <UserX className="h-3 w-3" /> : <UserCheck className="h-3 w-3" />}
                              </button>

                            </div>
                          </td>
                          <td className="table-cell">
                            <div className="flex items-center">
                              <Crown className="h-4 w-4 text-yellow-600 mr-2" />
                              <div>
                                <div className="text-sm font-medium text-slate-900">{superAdmin.name}</div>
                                <div className="text-xs text-yellow-600 font-medium">Super Administrator</div>
                              </div>
                            </div>
                          </td>
                          <td className="table-cell">
                            <span className="text-sm text-slate-900">{superAdmin.email}</span>
                          </td>
                          <td className="table-cell">
                            <button
                              onClick={() => handleToggleStatus(superAdmin)}
                              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                                superAdmin.enabled
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                  : 'bg-red-100 text-red-800 hover:bg-red-200'
                              } transition-colors duration-200`}
                            >
                              {superAdmin.enabled ? (
                                <>
                                  <UserCheck className="h-3 w-3 mr-1" />
                                  Active
                                </>
                              ) : (
                                <>
                                  <UserX className="h-3 w-3 mr-1" />
                                  Inactive
                                </>
                              )}
                            </button>
                          </td>
                          <td className="table-cell">
                            <span className="text-sm text-slate-600">
                              {superAdmin.lastLogin ? new Date(superAdmin.lastLogin).toLocaleDateString() : 'Never'}
                            </span>
                          </td>
                          <td className="table-cell">
                            <span className="text-sm text-slate-600">
                              {new Date(superAdmin.createdAt).toLocaleDateString()}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="px-4 sm:px-6 py-4 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <span className="text-sm text-slate-700 text-center sm:text-left">
                  Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} super admins
                </span>
                <div className="flex items-center justify-center sm:justify-start space-x-2">
                  <label className="text-sm text-slate-700">Show:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                    className="input-field py-1 text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="btn-secondary flex items-center disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </button>

                <span className="text-sm text-slate-700">
                  Page {currentPage + 1} of {totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="btn-secondary flex items-center disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Super Admin Modal */}
      {showModal && (
        <SuperAdminModal
          isOpen={showModal}
          mode={modalMode}
          superAdmin={selectedSuperAdmin}
          onClose={() => setShowModal(false)}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};

export default SuperAdminManagement;
